import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import TextButton from 'LuxKit/Button/TextButton'
import SolidLockAltIcon from 'LuxKit/Icons/solid/SolidLockAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'

const PREVIEW_IMAGE_IDS = [
  'vg4uvt2y5dgkivujuba6',
  'dapbr82l65bcj5iy6v8',
  'lids8h5rs9zj2xvkxr',
] as const

const Banner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${rem(108)};
  overflow: hidden;
  padding: ${rem(20)};
  border-radius: ${rem(8)};
  background: linear-gradient(90deg, #304DAB 14.364%, #4C82DF 85.903%);
  color: ${props => props.theme.palette.neutral.surface.staticWhite};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(108)};
    gap: ${rem(40)};
    border: 2px solid #304DAB;
    padding: ${rem(24)} ${rem(20)};
  }
`

const Content = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  gap: ${rem(20)};
`

const TextContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  gap: ${rem(4)};
`

const BannerHeading = styled(Heading)`
  margin: 0;
  color: inherit;
  word-break: break-word;
`

const DesktopHeading = styled(BannerHeading)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const MobileHeading = styled(BodyText)`
  display: block;
  margin: 0;
  color: inherit;
  word-break: break-word;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Highlight = styled.span`
  color: #00F1BD;
`

const Body = styled(BodyText)`
  margin: 0;
  color: inherit;
  word-break: break-word;
`

const DesktopBody = styled(Body)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const MobileBody = styled(Body)`
  display: block;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const InlineCta = styled.a`
  margin: 0;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: ${props => props.theme.font.primary.weight.bold};
  text-decoration: underline;
  text-underline-position: from-font;
`

const LUX_PLUS_LANDING_PAGE_PATH = `/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`

const DesktopCta = styled(TextButton)`
  && {
    display: none;
    flex: 0 0 auto;

    ${mediaQueryUp.tablet} {
      display: inline-flex;
      min-width: ${rem(139)};
    }
  }
`

const ImageStack = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 ${rem(66)};
  align-items: center;
  width: ${rem(66)};
  height: ${rem(41)};
  order: 2;

  ${mediaQueryUp.tablet} {
    order: 0;
    flex-basis: ${rem(100)};
    width: ${rem(100)};
    height: ${rem(66)};
  }
`

const ImageTile = styled.div<{ $variant: 'left' | 'right' | 'centre' }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.staticWhite};
  border-radius: ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: 0 ${rem(2)} ${rem(6)} rgba(0, 0, 0, 0.18);

  ${props => props.$variant === 'left' && `
    left: 0;
    width: ${rem(32.66)};
    height: ${rem(32.66)};
    transform: rotate(-15deg);
  `}

  ${props => props.$variant === 'right' && `
    right: 0;
    width: ${rem(32.66)};
    height: ${rem(32.66)};
    transform: rotate(15deg);
  `}

  ${props => props.$variant === 'centre' && `
    left: ${rem(12.89)};
    top: ${rem(-0.83)};
    z-index: 1;
    width: ${rem(39.68)};
    height: ${rem(39.68)};
  `}

  ${mediaQueryUp.tablet} {
    border-radius: ${rem(12)};

    ${props => props.$variant === 'left' && `
      width: ${rem(49.39)};
      height: ${rem(49.39)};
    `}

    ${props => props.$variant === 'right' && `
      right: 0;
      width: ${rem(49.39)};
      height: ${rem(49.39)};
    `}

    ${props => props.$variant === 'centre' && `
      left: ${rem(19.48)};
      top: ${rem(-1.26)};
      width: ${rem(60)};
      height: ${rem(60)};
      border-width: 1.5px;
    `}
  }
`

const PreviewImage = styled(Image)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.32);
`

const TileLock = styled(SolidLockAltIcon)<{ $large?: boolean }>`
  position: relative;
  z-index: 1;
  width: ${props => props.$large ? rem(23.62) : rem(15.87)};
  height: ${props => props.$large ? rem(23.62) : rem(15.87)};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};

  ${mediaQueryUp.tablet} {
    width: ${props => props.$large ? rem(54) : rem(24)};
    height: ${props => props.$large ? rem(54) : rem(24)};
  }
`

interface Props {
  destinationName?: string
  savingPercent: number
}

function HiddenPreviewSearchResultBanner({ destinationName, savingPercent }: Props) {
  return (
    <Banner className="flush-banner" data-testid="hidden-preview-search-result-banner">
      <Content>
        <ImageStack aria-hidden="true">
          <ImageTile $variant="left">
            <PreviewImage id={PREVIEW_IMAGE_IDS[0]} alt="" fit="center" dpr={2} />
            <ImageOverlay />
            <TileLock aria-hidden="true" />
          </ImageTile>
          <ImageTile $variant="right">
            <PreviewImage id={PREVIEW_IMAGE_IDS[1]} alt="" fit="center" dpr={2} />
            <ImageOverlay />
            <TileLock aria-hidden="true" />
          </ImageTile>
          <ImageTile $variant="centre">
            <PreviewImage id={PREVIEW_IMAGE_IDS[2]} alt="" fit="center" dpr={2} />
            <ImageOverlay />
            <TileLock $large aria-hidden="true" />
          </ImageTile>
        </ImageStack>

        <TextContent>
          <DesktopHeading variant="heading5">
            {destinationName ? (
              <FormattedMessage
                id="6PUP48VXkuVsFtnHPSbZ"
                defaultMessage="Unlock hidden {destinationName} offers <highlight>with savings up to {savingPercent}%</highlight>"
                description="Desktop heading for the hotel search hidden-offer preview banner when a destination name is available"
                values={{
                  destinationName,
                  savingPercent,
                  highlight: (chunks) => <Highlight>{chunks}</Highlight>,
                }}
              />
            ) : (
              <FormattedMessage
                id="c5_Ef0AMR0JeEnu-7pH0"
                defaultMessage="Unlock hidden offers <highlight>with savings up to {savingPercent}%</highlight>"
                description="Desktop heading for the hotel search hidden-offer preview banner when no destination name is available"
                values={{
                  savingPercent,
                  highlight: (chunks) => <Highlight>{chunks}</Highlight>,
                }}
              />
            )}
          </DesktopHeading>

          <MobileHeading variant="medium" weight="bold">
            {destinationName ? (
              <FormattedMessage
                id="OXSWT5476LDElSFMSipo"
                defaultMessage="Unlock hidden {destinationName} offers with savings up to {savingPercent}%"
                description="Mobile heading for the hotel search hidden-offer preview banner when a destination name is available"
                values={{ destinationName, savingPercent }}
              />
            ) : (
              <FormattedMessage
                id="VOMSt7xJDxorFBdtbC0C"
                defaultMessage="Unlock hidden offers with savings up to {savingPercent}%"
                description="Mobile heading for the hotel search hidden-offer preview banner when no destination name is available"
                values={{ savingPercent }}
              />
            )}
          </MobileHeading>

          <DesktopBody as="p" variant="medium">
            <FormattedMessage
              id="Wm8KV_aV9EwPjT1SXYZv"
              defaultMessage="Start your LuxPlus+ free preview to reveal member-only offers you wouldn’t otherwise see."
              description="Desktop body copy for the hotel search hidden-offer preview banner explaining the free preview benefit"
            />
          </DesktopBody>

          <MobileBody as="p" variant="small">
            <FormattedMessage
              id="AN1q25vGieutZturval7"
              defaultMessage="Start your LuxPlus+ free preview to reveal member-only offers. <cta>Start Free Preview</cta>"
              description="Mobile body copy for the hotel search hidden-offer preview banner with an inline free preview action"
              values={{
                cta: (chunks) => (
                  <InlineCta
                    key="hidden-preview-mobile-cta"
                    href={LUX_PLUS_LANDING_PAGE_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </InlineCta>
                ),
              }}
            />
          </MobileBody>
        </TextContent>
      </Content>

      <ProductPaletteProvider palette="lux-plus">
        <DesktopCta
          kind="primary"
          variant="ghost"
          size="medium"
          to={LUX_PLUS_LANDING_PAGE_PATH}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage
            id="0SqlRnnBg3AH0a6rhfsN"
            defaultMessage="Start Free Preview"
            description="Button text to open the LuxPlus free preview modal from the hotel search hidden-offer preview banner"
          />
        </DesktopCta>
      </ProductPaletteProvider>
    </Banner>
  )
}

export default HiddenPreviewSearchResultBanner
